import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import type { OnboardingData, InviteInput } from './types.js';

interface Props {
  data: OnboardingData;
  onChange: (patch: Partial<OnboardingData>) => void;
  onBack: () => void;
  onNext: () => void;
  submitting: boolean;
}

export default function StepInvite({ data, onChange, onBack, onNext, submitting }: Props) {
  const { t } = useTranslation('onboarding');

  function addInvite() {
    onChange({ invites: [...data.invites, { email: '', role: 'member' }] });
  }

  function updateInvite(index: number, patch: Partial<InviteInput>) {
    onChange({
      invites: data.invites.map((inv, i) => (i === index ? { ...inv, ...patch } : inv)),
    });
  }

  function removeInvite(index: number) {
    onChange({ invites: data.invites.filter((_, i) => i !== index) });
  }

  return (
    <div className="page-enter">
      <h2
        style={{
          fontSize: 'var(--text-2xl)',
          fontWeight: 'var(--weight-light)',
          color: 'var(--color-text)',
          marginBottom: 'var(--space-2)',
          letterSpacing: 'var(--tracking-tight)',
        }}
      >
        {t('invite.title')}
      </h2>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>
        {t('invite.subtitle')}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
        {data.invites.map((invite, index) => (
          <div key={index} style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <input
              type="email"
              value={invite.email}
              onChange={(e) => updateInvite(index, { email: e.target.value })}
              placeholder={t('invite.email_placeholder')}
              style={{
                flex: 1,
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text)',
                background: 'var(--color-bg)',
              }}
            />
            <select
              value={invite.role}
              onChange={(e) => updateInvite(index, { role: e.target.value as InviteInput['role'] })}
              aria-label={t('invite.role_label')}
              style={{
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text)',
                background: 'var(--color-bg)',
              }}
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
              <option value="viewer">Viewer</option>
            </select>
            <button
              onClick={() => removeInvite(index)}
              aria-label="Remove invite"
              style={{
                background: 'none',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                color: 'var(--color-gray-400)',
                display: 'flex',
                alignItems: 'center',
                padding: 'var(--space-2)',
              }}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      {data.invites.length < 5 && (
        <button
          onClick={addInvite}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            border: '1px dashed var(--color-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
            color: 'var(--color-text-secondary)',
            background: 'var(--color-bg)',
            cursor: 'pointer',
            marginBottom: 'var(--space-8)',
            width: '100%',
          }}
        >
          <Plus size={14} />
          {t('invite.add_another')}
        </button>
      )}

      {data.invites.length === 0 && <div style={{ marginBottom: 'var(--space-8)' }} />}

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <button onClick={onBack} style={backBtnStyle} disabled={submitting}>
          ← {t('actions.back', { ns: 'common' })}
        </button>
        <button
          onClick={onNext}
          disabled={submitting}
          style={{
            flex: 1,
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-brand)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)',
            cursor: submitting ? 'not-allowed' : 'pointer',
            opacity: submitting ? 0.7 : 1,
          }}
        >
          {submitting ? t('actions.loading', { ns: 'common' }) : (data.invites.length > 0 ? t('invite.send') : t('actions.done', { ns: 'common' }))}
        </button>
      </div>
    </div>
  );
}

const backBtnStyle: React.CSSProperties = {
  padding: 'var(--space-3) var(--space-5)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-bg)',
  cursor: 'pointer',
};
